import { FC, useState } from 'react';
import classNames from 'classnames';
import { Switch } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChevronRight,
  faLock,
  faTrash,
  faTriangleExclamation,
  faUserGroup,
} from '@fortawesome/pro-solid-svg-icons';
import { useTranslations } from 'next-intl';
import { Box } from 'models/Box';
import { AccessLevel } from 'models/AccessLevel';
import useBoxes from 'networking/mindfeed/hooks/useBoxes';
import useDeleteBox from 'networking/mindfeed/hooks/useDeleteBox';
import { useSaveBox } from 'networking/mindfeed/hooks/useSaveBox';
import { useUpdateBoxSettings } from 'networking/mindfeed/hooks/useUpdateBoxSettings';
import type { BoxSettingsPatch } from 'networking/mindfeed/services/box.service';
import { openActionableModal, openBoxModal } from 'xstate/actors/ModalActor';
import { BoxModalStep } from 'xstate/machines/ModalMachine';
import RadioField, { RadioFieldOption } from 'elements/RadioField/RadioField';
import Button from 'elements/Button';

export type BoxSettingsStep =
  | BoxModalStep.Settings
  | BoxModalStep.SettingsAccess
  | BoxModalStep.SettingsDanger;

interface Props {
  box: Box;
  step: BoxSettingsStep;
}

// Owner-only settings for a box
const BoxSettingsForm: FC<Props> = ({ box: initialBox, step }) => {
  // The modal payload is a snapshot taken when it opened. Read the live box
  // from the cache so a flipped switch shows its new state.
  const { data: boxes } = useBoxes();
  const box = boxes?.find((b) => b.id === initialBox.id) ?? initialBox;

  switch (step) {
    case BoxModalStep.SettingsAccess:
      return <AccessEditor box={box} />;
    case BoxModalStep.SettingsDanger:
      return <DangerZone box={box} />;
    default:
      return <Hub box={box} />;
  }
};

export default BoxSettingsForm;

const Hub: FC<{ box: Box }> = ({ box }) => {
  const t = useTranslations();
  const [hasFailed, setHasFailed] = useState(false);
  const settings = box.settings;
  const isShared = box.accessLevel === AccessLevel.Shared;

  const updateSettings = useUpdateBoxSettings({
    onError: () => setHasFailed(true),
  });

  const flip = (patch: BoxSettingsPatch) => {
    setHasFailed(false);
    updateSettings.mutate({ boxId: box.id, patch });
  };

  const accessDescription = `${isShared ? t('boxSettings.shared') : t('boxSettings.private')} · ${
    isShared
      ? t('boxSettings.sharedSubtitle')
      : t('boxSettings.privateSubtitle')
  }`;

  return (
    <div className={styles.container}>
      <section>
        <h3 className={styles.sectionLabel}>{t('boxSettings.accessHeader')}</h3>
        <NavRow
          icon={isShared ? faUserGroup : faLock}
          iconClassName={styles.icon.accent}
          title={t('boxSettings.accessTitle')}
          subtitle={accessDescription}
          onClick={() =>
            openBoxModal(box, { step: BoxModalStep.SettingsAccess })
          }
        />
      </section>

      <section>
        <h3 className={styles.sectionLabel}>
          {t('boxSettings.allowedHeader')}
        </h3>
        <div className={styles.card}>
          <ToggleRow
            label={t('boxSettings.comments')}
            checked={settings.commentsEnabled}
            disabled={updateSettings.isPending}
            onChange={(checked) => flip({ commentsEnabled: checked })}
          />
          <div className={styles.divider} />
          <ToggleRow
            label={t('boxSettings.reactions')}
            checked={settings.reactionsEnabled}
            disabled={updateSettings.isPending}
            onChange={(checked) => flip({ reactionsEnabled: checked })}
          />
        </div>
        {hasFailed && (
          <p role="alert" className={styles.error}>
            {t('boxSettings.error')}
          </p>
        )}
      </section>

      <NavRow
        icon={faTriangleExclamation}
        iconClassName={styles.icon.danger}
        title={t('boxSettings.dangerTitle')}
        subtitle={t('boxSettings.dangerSubtitle')}
        onClick={() => openBoxModal(box, { step: BoxModalStep.SettingsDanger })}
      />
    </div>
  );
};

const AccessEditor: FC<{ box: Box }> = ({ box }) => {
  const t = useTranslations();
  const [draft, setDraft] = useState<AccessLevel>(box.accessLevel);
  const [hasFailed, setHasFailed] = useState(false);

  const backToHub = (target: Box = box) =>
    openBoxModal(target, { step: BoxModalStep.Settings });

  // A shared box goes on to Manage Access, which is the point of sharing.
  const complete = (saved: Box) => {
    if (saved.accessLevel === AccessLevel.Shared) {
      openBoxModal(saved, {
        step: BoxModalStep.ManagePermissions,
        canNavigateBack: false,
      });
    } else {
      backToHub(saved);
    }
  };

  const saveBox = useSaveBox({ onSuccess: complete });

  const onSave = async () => {
    if (draft === box.accessLevel) {
      complete(box);
      return;
    }
    setHasFailed(false);
    try {
      await saveBox.mutateAsync({ box: { ...box, accessLevel: draft } });
    } catch {
      setHasFailed(true);
    }
  };

  const options: RadioFieldOption[] = [
    {
      value: AccessLevel.Private,
      title: t('boxSettings.private'),
      subtitle: t('boxSettings.privateSubtitle'),
      icon: faLock,
    },
    {
      value: AccessLevel.Shared,
      title: t('boxSettings.shared'),
      subtitle: t('boxSettings.sharedSubtitle'),
      icon: faUserGroup,
    },
  ];

  return (
    <div className={styles.container}>
      <RadioField
        value={draft}
        options={options}
        onChange={(value) => setDraft(value as AccessLevel)}
      />
      {hasFailed && (
        <p role="alert" className={styles.error}>
          {t('boxSettings.error')}
        </p>
      )}
      <div className={styles.footer}>
        <Button
          type="button"
          size="sm"
          variant="solid"
          color="neutral"
          onClick={() => backToHub()}
          disabled={saveBox.isPending}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={saveBox.isPending}
          loading={saveBox.isPending}
          loadingText={t('common.save')}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
};

const DangerZone: FC<{ box: Box }> = ({ box }) => {
  const t = useTranslations();
  const deleteBox = useDeleteBox({ removeOnSuccess: false });

  const confirmDelete = () =>
    openActionableModal({
      title: t('boxSettings.deleteConfirmTitle'),
      bodyElement: (
        <div className="my-5">
          <span className="block my-2">
            {t('boxSettings.deleteConfirmBody')}
          </span>
          <span className="block my-2 text-red-500">
            {t('boxSettings.deleteConfirmWarning')}
          </span>
        </div>
      ),
      onAccept: () => deleteBox.mutate(box),
      acceptLabel: t('boxSettings.deleteConfirmButton'),
    });

  return (
    <div className={styles.container}>
      <div>
        <button
          type="button"
          className={classNames(styles.row.base, styles.row.clickable)}
          onClick={confirmDelete}
        >
          <span className={classNames(styles.icon.base, styles.icon.danger)}>
            <FontAwesomeIcon icon={faTrash} className="fa-fw" />
          </span>
          <span className={styles.row.dangerTitle}>
            {t('boxSettings.dangerDelete')}
          </span>
        </button>
        <p className={styles.footnote}>{t('boxSettings.dangerFooter')}</p>
      </div>
    </div>
  );
};

const NavRow: FC<{
  icon: IconProp;
  iconClassName: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}> = ({ icon, iconClassName, title, subtitle, onClick }) => (
  <button
    type="button"
    className={classNames(styles.row.base, styles.row.clickable)}
    onClick={onClick}
  >
    <span className={classNames(styles.icon.base, iconClassName)}>
      <FontAwesomeIcon icon={icon} className="fa-fw" />
    </span>
    <span className={styles.row.text}>
      <span className={styles.row.title}>{title}</span>
      <span className={styles.row.subtitle}>{subtitle}</span>
    </span>
    <FontAwesomeIcon
      icon={faChevronRight}
      className={styles.row.chevron}
      aria-hidden="true"
    />
  </button>
);

const ToggleRow: FC<{
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, disabled, onChange }) => (
  <Switch.Group as="div" className={styles.toggle.row}>
    <Switch.Label className={styles.toggle.label}>{label}</Switch.Label>
    <Switch
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className={classNames(
        styles.toggle.track,
        checked ? styles.toggle.on : styles.toggle.off,
      )}
    >
      <span
        aria-hidden="true"
        className={classNames(
          styles.toggle.thumb,
          checked ? 'translate-x-[22px]' : 'translate-x-0.5',
        )}
      />
    </Switch>
  </Switch.Group>
);

const styles = {
  container: 'px-6 space-y-6',
  sectionLabel: 'mb-3 text-[15px] font-bold text-mf-off-black-950',
  card: 'rounded-2xl border border-gray-200 bg-gray-50/60',
  divider: 'mx-3 border-t border-gray-200',
  error: 'mt-3 text-sm text-red-500',
  footer: 'flex justify-end gap-3',
  footnote: 'mt-3 text-sm text-gray-500',
  icon: {
    base: 'flex-none flex items-center justify-center w-10 h-10 rounded-lg',
    accent: 'bg-mf-blue-50 text-mf-blue',
    danger: 'bg-red-50 text-red-500',
  },
  row: {
    base: classNames(
      'flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/60 p-3 text-left',
    ),
    clickable:
      'transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-mf-blue',
    text: 'flex-1 min-w-0',
    title: 'block font-semibold text-mf-off-black-950',
    subtitle: 'block text-sm text-gray-500 truncate',
    dangerTitle: 'flex-1 font-semibold text-red-500',
    chevron: 'flex-none text-xs text-gray-400',
  },
  toggle: {
    row: 'flex items-center justify-between gap-3 px-3 py-3',
    label: 'text-[15px] text-mf-off-black-950',
    track:
      'relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-mf-blue focus-visible:ring-offset-2 disabled:opacity-60',
    on: 'bg-mf-blue',
    off: 'bg-gray-300',
    thumb:
      'inline-block h-5 w-5 rounded-full bg-white shadow transition-transform',
  },
};
